import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car, ChevronsUpDown, Check, Download, FileSpreadsheet, File, FileType, FileJson, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL;

const VehiculosRegistro = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [regVehFilters, setRegVehFilters] = useState({ rif: "", nombre: "", placa: "", anio: "", marca: "", modelo: "", color: "", uso: "", tipo: "", ultimo_anio: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({});
  const [contribuyentes, setContribuyentes] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [clasificadorVeh, setClasificadorVeh] = useState([]);

  const fetchRegistros = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/registro`, { headers });
      setItems(r.data);
    } catch { toast.error("Error al cargar registros vehiculares"); }
  };

  const fetchContribuyentes = async (clase) => {
    try {
      const endpoint = clase === "Contribuyente sin LAE" ? "sin-lae" : "con-lae";
      const r = await axios.get(`${API}/api/contribuyentes/${endpoint}`, { headers });
      setContribuyentes(r.data);
    } catch { setContribuyentes([]); }
  };

  const fetchClasificadorVeh = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/clasificador`, { headers });
      setClasificadorVeh(r.data);
    } catch { setClasificadorVeh([]); }
  };

  useEffect(() => {
    fetchRegistros();
  }, []);

  const openNew = () => {
    setEditingItem(null);
    setFormData({ clase_contribuyente: "Contribuyente con LAE" });
    fetchContribuyentes("Contribuyente con LAE");
    fetchClasificadorVeh();
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    fetchContribuyentes(item.clase_contribuyente);
    fetchClasificadorVeh();
    setDialogOpen(true);
  };

  const resetForm = () => { setFormData({}); setEditingItem(null); };

  const handleSave = async () => {
    if (!formData.rif) { toast.error("RIF / Contribuyente es obligatorio"); return; }
    if (!formData.placa) { toast.error("Placa es obligatoria"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/api/vehiculos/registro/${editingItem.id}`, formData, { headers });
        toast.success("Registro vehicular actualizado");
      } else {
        await axios.post(`${API}/api/vehiculos/registro`, formData, { headers });
        toast.success("Registro vehicular creado");
      }
      setDialogOpen(false); resetForm(); fetchRegistros();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/vehiculos/registro/${itemToDelete.id}`, { headers });
      toast.success("Registro eliminado");
      setDeleteDialogOpen(false); setItemToDelete(null);
      fetchRegistros();
    } catch { toast.error("Error al eliminar"); }
  };

  const selectContribuyente = (c) => {
    setFormData({ ...formData, rif: c.rif, nombre_razon_social: c.nombre || c.nombre_razon_social || "" });
    setRifPopoverOpen(false);
  };

  const filtered = items.filter(i => {
    const f = regVehFilters;
    if (f.rif && !(i.rif || "").toLowerCase().includes(f.rif.toLowerCase())) return false;
    if (f.nombre && !(i.nombre_razon_social || "").toLowerCase().includes(f.nombre.toLowerCase())) return false;
    if (f.placa && !(i.placa || "").toLowerCase().includes(f.placa.toLowerCase())) return false;
    if (f.anio && !String(i.anio || "").includes(f.anio)) return false;
    if (f.marca && !(i.marca || "").toLowerCase().includes(f.marca.toLowerCase())) return false;
    if (f.modelo && !(i.modelo || "").toLowerCase().includes(f.modelo.toLowerCase())) return false;
    if (f.color && !(i.color || "").toLowerCase().includes(f.color.toLowerCase())) return false;
    if (f.uso && !(i.uso || "").toLowerCase().includes(f.uso.toLowerCase())) return false;
    if (f.tipo && !(i.tipo_clasificacion || "").toLowerCase().includes(f.tipo.toLowerCase())) return false;
    if (f.ultimo_anio && !String(i.ultimo_anio_pago || "").includes(f.ultimo_anio)) return false;
    return true;
  });

  // ============ EXPORT ============
  const getExportData = () => filtered.map(item => ({
    "RIF": item.rif || "",
    "Contribuyente": item.nombre_razon_social || "",
    "Placa": item.placa || "",
    "Año": item.anio || "",
    "Marca": item.marca || "",
    "Modelo": item.modelo || "",
    "Color": item.color || "",
    "Uso": item.uso || "",
    "Tipo/Clasificación": item.tipo_clasificacion || "",
    "Último Año Pago": item.ultimo_anio_pago || ""
  }));

  const exportToExcel = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const ws = XLSX.utils.json_to_sheet(d); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Registro Vehicular"); XLSX.writeFile(wb, `registro_vehicular_${new Date().toISOString().split("T")[0]}.xlsx`); toast.success("Exportado a Excel exitosamente"); };
  const exportToPDF = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const doc = new jsPDF("landscape"); doc.setFontSize(16); doc.text("Registro Vehicular", 14, 20); doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")}`, 14, 28); autoTable(doc, { head: [Object.keys(d[0])], body: d.map(r => Object.values(r)), startY: 35, styles: { fontSize: 7 }, headStyles: { fillColor: [37, 99, 235] } }); doc.save(`registro_vehicular_${new Date().toISOString().split("T")[0]}.pdf`); toast.success("Exportado a PDF exitosamente"); };
  const exportToCSV = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const h = Object.keys(d[0]).join(","); const rows = d.map(r => Object.values(r).map(v => `"${v}"`).join(",")).join("\n"); saveAs(new Blob(["\ufeff" + h + "\n" + rows], { type: "text/csv;charset=utf-8" }), `registro_vehicular_${new Date().toISOString().split("T")[0]}.csv`); toast.success("Exportado a CSV exitosamente"); };
  const exportToText = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const cw = hs.map(h => Math.max(h.length, ...d.map(r => String(r[h]).length))); let t = "REGISTRO VEHICULAR\n"; t += `Fecha: ${new Date().toLocaleDateString("es-VE")}\n`; t += "=".repeat(Math.max(cw.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n"; t += hs.map((h, i) => h.padEnd(cw[i])).join(" | ") + "\n"; t += cw.map(w => "-".repeat(w)).join("-+-") + "\n"; d.forEach(r => { t += hs.map((h, i) => String(r[h]).padEnd(cw[i])).join(" | ") + "\n"; }); saveAs(new Blob([t], { type: "text/plain;charset=utf-8" }), `registro_vehicular_${new Date().toISOString().split("T")[0]}.txt`); toast.success("Exportado a Texto exitosamente"); };
  const exportToHTML = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Registro Vehicular</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#2563eb}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:10px;text-align:left;font-size:12px}th{background-color:#2563eb;color:white}tr:nth-child(even){background-color:#f9fafb}tr:hover{background-color:#f3f4f6}.fecha{color:#6b7280;margin-bottom:20px}</style></head><body><h1>Registro Vehicular</h1><p class="fecha">Fecha: ${new Date().toLocaleDateString("es-VE")}</p><table><thead><tr>${hs.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${d.map(r => `<tr>${Object.values(r).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`; saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), `registro_vehicular_${new Date().toISOString().split("T")[0]}.html`); toast.success("Exportado a HTML exitosamente"); };
  const exportToJSON = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } saveAs(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }), `registro_vehicular_${new Date().toISOString().split("T")[0]}.json`); toast.success("Exportado a JSON exitosamente"); };

  return (
    <div className="space-y-6" data-testid="vehiculos-registro">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Registro Vehicular</h1>
          <p className="text-slate-500">Gestión de registro vehicular de contribuyentes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filtered.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{items.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-exportar-reg-veh"><Download className="w-4 h-4" /> Exportar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> Texto</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" /> HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo-vehiculo">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>RIF</TableHead>
              <TableHead>Contribuyente</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Año</TableHead>
              <TableHead>Marca</TableHead>
              <TableHead>Modelo</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Uso</TableHead>
              <TableHead>Tipo / Clasificación</TableHead>
              <TableHead>Últ. Año Pago</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
            <TableRow className="bg-slate-50/50 border-b">
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.rif} onChange={(e) => setRegVehFilters({...regVehFilters, rif: e.target.value})} data-testid="filter-regveh-rif" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.nombre} onChange={(e) => setRegVehFilters({...regVehFilters, nombre: e.target.value})} data-testid="filter-regveh-nombre" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.placa} onChange={(e) => setRegVehFilters({...regVehFilters, placa: e.target.value})} data-testid="filter-regveh-placa" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.anio} onChange={(e) => setRegVehFilters({...regVehFilters, anio: e.target.value})} data-testid="filter-regveh-anio" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.marca} onChange={(e) => setRegVehFilters({...regVehFilters, marca: e.target.value})} data-testid="filter-regveh-marca" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.modelo} onChange={(e) => setRegVehFilters({...regVehFilters, modelo: e.target.value})} data-testid="filter-regveh-modelo" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.color} onChange={(e) => setRegVehFilters({...regVehFilters, color: e.target.value})} data-testid="filter-regveh-color" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.uso} onChange={(e) => setRegVehFilters({...regVehFilters, uso: e.target.value})} data-testid="filter-regveh-uso" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.tipo} onChange={(e) => setRegVehFilters({...regVehFilters, tipo: e.target.value})} data-testid="filter-regveh-tipo" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={regVehFilters.ultimo_anio} onChange={(e) => setRegVehFilters({...regVehFilters, ultimo_anio: e.target.value})} data-testid="filter-regveh-ultimo-anio" /></TableHead>
              <TableHead className="px-2 py-1.5"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-400">No hay registros vehiculares</TableCell></TableRow>
            ) : filtered.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">{item.rif}</TableCell>
                <TableCell className="text-sm">{item.nombre_razon_social || "-"}</TableCell>
                <TableCell className="text-sm font-medium">{item.placa || "-"}</TableCell>
                <TableCell className="text-sm">{item.anio || "-"}</TableCell>
                <TableCell className="text-sm">{item.marca || "-"}</TableCell>
                <TableCell className="text-sm">{item.modelo || "-"}</TableCell>
                <TableCell className="text-sm">{item.color || "-"}</TableCell>
                <TableCell className="text-sm">{item.uso || "-"}</TableCell>
                <TableCell className="text-sm">{item.tipo_clasificacion || "-"}</TableCell>
                <TableCell className="text-sm">{item.ultimo_anio_pago || "-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`btn-edit-veh-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} data-testid={`btn-delete-veh-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog - Registro */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              {editingItem ? "Editar Registro Vehicular" : "Nuevo Registro Vehicular"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Clase de Contribuyente</Label>
              <Select value={formData.clase_contribuyente || ""} onValueChange={(v) => { setFormData({ ...formData, clase_contribuyente: v, rif: "", nombre_razon_social: "" }); fetchContribuyentes(v); }}>
                <SelectTrigger data-testid="form-clase-contrib-veh"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Contribuyente con LAE">Contribuyente con LAE</SelectItem>
                  <SelectItem value="Contribuyente sin LAE">Contribuyente sin LAE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>RIF / Contribuyente *</Label>
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-rif-veh">
                    {formData.rif ? `${formData.rif} - ${formData.nombre_razon_social || ""}` : <span className="text-slate-500">Buscar contribuyente...</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por RIF o nombre..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron contribuyentes</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.map(c => (
                          <CommandItem key={c.id || c.rif} value={`${c.rif} ${c.nombre || c.nombre_razon_social || ""}`} onSelect={() => selectContribuyente(c)}>
                            <Check className={`mr-2 h-4 w-4 ${formData.rif === c.rif ? "opacity-100" : "opacity-0"}`} />
                            <span className="font-mono text-xs">{c.rif}</span>
                            <span className="ml-2 text-sm text-slate-600 truncate">{c.nombre || c.nombre_razon_social || ""}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Placa *</Label>
                <Input value={formData.placa || ""} onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })} placeholder="Ej: ABC123" data-testid="form-placa" />
              </div>
              <div className="space-y-2">
                <Label>Año</Label>
                <Input type="number" value={formData.anio || ""} onChange={(e) => setFormData({ ...formData, anio: e.target.value })} placeholder="Ej: 2024" data-testid="form-anio-veh" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marca</Label>
                <Input value={formData.marca || ""} onChange={(e) => setFormData({ ...formData, marca: e.target.value })} placeholder="Ej: Toyota" data-testid="form-marca" />
              </div>
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input value={formData.modelo || ""} onChange={(e) => setFormData({ ...formData, modelo: e.target.value })} placeholder="Ej: Corolla" data-testid="form-modelo" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Input value={formData.color || ""} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="Ej: Blanco" data-testid="form-color" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serial Motor</Label>
                <Input value={formData.serial_motor || ""} onChange={(e) => setFormData({ ...formData, serial_motor: e.target.value.toUpperCase() })} placeholder="Serial del motor" data-testid="form-serial-motor" />
              </div>
              <div className="space-y-2">
                <Label>Serial Carrocería</Label>
                <Input value={formData.serial_carroceria || ""} onChange={(e) => setFormData({ ...formData, serial_carroceria: e.target.value.toUpperCase() })} placeholder="Serial de carrocería" data-testid="form-serial-carroceria" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Uso</Label>
                <Select value={formData.uso || ""} onValueChange={(v) => setFormData({ ...formData, uso: v })}>
                  <SelectTrigger data-testid="form-uso-veh"><SelectValue placeholder="Seleccione uso" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Particular">Particular</SelectItem>
                    <SelectItem value="Comercial">Comercial</SelectItem>
                    <SelectItem value="Oficial">Oficial</SelectItem>
                    <SelectItem value="Transporte Público">Transporte Público</SelectItem>
                    <SelectItem value="Carga">Carga</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo o Clasificación del Vehículo</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-tipo-veh">
                      {formData.tipo_clasificacion || <span className="text-slate-500">Seleccione tipo</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar tipo de vehículo..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron tipos</CommandEmpty>
                        <CommandGroup>
                          {clasificadorVeh.map(c => (
                            <CommandItem key={c.id} value={c.tipo_clase_vehiculo} onSelect={() => setFormData({ ...formData, tipo_clasificacion: c.tipo_clase_vehiculo })}>
                              <Check className={`mr-2 h-4 w-4 ${formData.tipo_clasificacion === c.tipo_clase_vehiculo ? "opacity-100" : "opacity-0"}`} />
                              {c.tipo_clase_vehiculo}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Último año de pago</Label>
              <Input type="number" value={formData.ultimo_anio_pago || ""} onChange={(e) => setFormData({ ...formData, ultimo_anio_pago: parseInt(e.target.value) || null })} placeholder="Ej: 2025" className="max-w-[200px]" data-testid="form-ultimo-anio-pago-veh" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setDialogOpen(false); }}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar-vehiculo">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar Eliminación</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">¿Está seguro de eliminar el registro vehicular de placa <strong>{itemToDelete?.placa}</strong>?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete-veh">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiculosRegistro;
